export const IS_NEW_KEY = "isNewNotifications";
export const NOT_NEW_KEY = "notNewNotifications";
export const NOTIFICATIONS_UNREAD_CHANGED_EVENT = "betto-notifications-unread-changed";

const normalizeSubmissionTime = (value) => (value == null ? "" : String(value));

const hasTextContent = (value) => {
	if (value == null) {
		return false;
	}

	return String(value).trim().length > 0;
};

export const isValidNotification = (notification) => {
	if (!notification) {
		return false;
	}

	if (notification._isTransaction) {
		return true;
	}

	return hasTextContent(notification.subject) && hasTextContent(notification.message);
};

export const filterValidNotifications = (notifications) =>
	(notifications || []).filter(isValidNotification);

export const normalizeNotificationSubmissionTime = normalizeSubmissionTime;

export const getNotificationId = (notification) => {
	if (!notification) {
		return null;
	}

	if (notification.notificationId) {
		return notification.notificationId;
	}

	if (notification.id && String(notification.id).includes("#")) {
		return notification.id;
	}

	const time = notification.time ?? notification.submissionTime;
	const username = notification.username;

	if (username && time) {
		return `${username}#${time}`;
	}

	try {
		const userData = JSON.parse(localStorage.getItem("user_data") || "{}");

		if (userData.username && time) {
			return `${userData.username}#${time}`;
		}
	} catch {
		// ignore invalid localStorage payload
	}

	return time ? String(time) : null;
};

export const isNotificationUnread = (notification) =>
	notification?.isNew === "true" || notification?.isNew === true;

export const getNotificationReadSets = () => ({
	isNew: new Set(
		(JSON.parse(localStorage.getItem(IS_NEW_KEY)) || []).map(normalizeSubmissionTime)
	),
	notNew: new Set(
		(JSON.parse(localStorage.getItem(NOT_NEW_KEY)) || []).map(normalizeSubmissionTime)
	),
});

export const emitUnreadCountChanged = (count) => {
	if (typeof window === "undefined") {
		return;
	}

	window.dispatchEvent(
		new CustomEvent(NOTIFICATIONS_UNREAD_CHANGED_EVENT, {
			detail: { count },
		})
	);
};

export const persistNotificationReadSets = (isNewSet, notNewSet) => {
	localStorage.setItem(IS_NEW_KEY, JSON.stringify(Array.from(isNewSet)));
	localStorage.setItem(NOT_NEW_KEY, JSON.stringify(Array.from(notNewSet)));
	emitUnreadCountChanged(isNewSet.size);
};

export const applyNotificationReadState = (items) => {
	const { isNew, notNew } = getNotificationReadSets();

	return (items || []).map((item) => {
		const submissionTime = normalizeSubmissionTime(item.submissionTime);

		if (isNew.has(submissionTime)) {
			return { ...item, isNew: "true" };
		}

		if (notNew.has(submissionTime)) {
			return { ...item, isNew: "false" };
		}

		if (item.isNew !== undefined) {
			return { ...item, isNew: String(item.isNew) };
		}

		return { ...item, isNew: "true" };
	});
};

export const syncNotificationsWithStorage = (notifications) => {
	const { isNew, notNew } = getNotificationReadSets();
	let changed = false;

	notifications.forEach((item) => {
		const submissionTime = normalizeSubmissionTime(item.submissionTime);

		if (isNew.has(submissionTime)) {
			item.isNew = "true";
			return;
		}

		if (notNew.has(submissionTime)) {
			item.isNew = "false";
			return;
		}

		const isUnreadOnBackend =
			item.isNew === undefined || item.isNew === "true" || item.isNew === true;

		if (isUnreadOnBackend) {
			item.isNew = "true";
			isNew.add(submissionTime);
		} else {
			item.isNew = "false";
			notNew.add(submissionTime);
		}
		changed = true;
	});

	if (changed) {
		persistNotificationReadSets(isNew, notNew);
	}

	return notifications;
};

export const updateNotificationReadStateInStorage = (submissionTime, isUnread) => {
	const normalizedSubmissionTime = normalizeSubmissionTime(submissionTime);

	if (!normalizedSubmissionTime) {
		return getNotificationReadSets().isNew.size;
	}

	const { isNew, notNew } = getNotificationReadSets();

	isNew.delete(normalizedSubmissionTime);
	notNew.delete(normalizedSubmissionTime);

	if (isUnread) {
		isNew.add(normalizedSubmissionTime);
	} else {
		notNew.add(normalizedSubmissionTime);
	}

	persistNotificationReadSets(isNew, notNew);

	return isNew.size;
};

export const getUnreadCountFromStorage = () =>
	(JSON.parse(localStorage.getItem(IS_NEW_KEY)) || []).length;
